import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Container
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { db } from '../../firebase';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  arrayUnion,
  getDoc
} from 'firebase/firestore';
import {
  GROUP_TYPES,
  VAULT_PERMISSIONS,
  PORTAL_PERMISSIONS,
} from '../../utils/defaultGroups';
import { getAuth } from 'firebase/auth';
import { familyGroupService } from '../../services/familyGroupService';
import logger from '../../utils/logger';

// Normalize group names to Title Case: "COUSINS" / "cousins" / "cOUSINS" → "Cousins"
// Preserves trailing whitespace so users can type multi-word names in real time.
const toTitleCase = (str) => {
  const trailing = str.match(/\s+$/)?.[0] || '';
  return str
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ') + trailing;
};

const GroupManagement = () => {
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [groupName, setGroupName] = useState('');
  const [groupType, setGroupType] = useState('');
  const [customPermissions, setCustomPermissions] = useState(false);
  const [vaultPermission, setVaultPermission] = useState(VAULT_PERMISSIONS.READ_ONLY);
  const [portalPermission, setPortalPermission] = useState(PORTAL_PERMISSIONS.VIEWER);

  const auth = getAuth();

  // Memoize loadGroups to prevent infinite loops
  const loadGroups = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const user = auth.currentUser;
      if (!user) {
        throw new Error('No authenticated user found');
      }

      // Get the family code for the current user
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) {
        throw new Error('User document not found');
      }
      const familyCode = userDoc.data().familyCode;

      if (!familyCode) {
        throw new Error('No family code found for user');
      }

      // Use familyGroupService to get groups from NESTED structure
      const familyGroups = await familyGroupService.getFamilyGroups(familyCode);

      // Fetch member details for each group
      const groupsWithDetails = [];
      for (const group of familyGroups) {
        const memberIds = group.members || [];
        const memberDetails = [];

        // Fetch members from NESTED structure: /families/{familyCode}/members/{userId}
        const familyRef = doc(db, 'families', familyCode);
        for (const memberId of memberIds) {
          try {
            const memberDoc = await getDoc(doc(familyRef, 'members', memberId));
            if (memberDoc.exists()) {
              const memberData = memberDoc.data();
              memberDetails.push({
                id: memberId,
                email: memberData.email,
                displayName: memberData.displayName || `${memberData.firstName || ''} ${memberData.lastName || ''}`.trim()
              });
            }
          } catch (memberErr) {
            logger.error(`Error fetching member ${memberId}:`, memberErr);
          }
        }

        groupsWithDetails.push({
          ...group,
          memberDetails
        });
      }

      setGroups(groupsWithDetails);
    } catch (err) {
      logger.error('Error loading groups:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [auth]);

  // Memoize loadUsers to prevent infinite loops
  const loadUsers = useCallback(async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        setError('No authenticated user');
        return;
      }

      // Get current user's familyCode
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        setError('User profile not found');
        return;
      }

      const familyCode = userDoc.data().familyCode;
      if (!familyCode) {
        setError('No family code found for user');
        return;
      }

      // Query members from NESTED structure: /families/{familyCode}/members
      const familyRef = doc(db, 'families', familyCode);
      const membersRef = collection(familyRef, 'members');
      const membersSnapshot = await getDocs(membersRef);

      const usersData = membersSnapshot.docs.map(doc => ({
        id: doc.id,
        email: doc.data().email,
        displayName: doc.data().displayName || `${doc.data().firstName} ${doc.data().lastName}`
      }));
      setUsers(usersData);
    } catch (err) {
      logger.error('Error loading users:', err);
      setError('Failed to load users');
    }
  }, [auth]);

  useEffect(() => {
    loadGroups();
    loadUsers();
  }, [loadGroups, loadUsers]); // Added dependencies

  // Reset form handler
  const resetForm = useCallback(() => {
    setGroupName('');
    setGroupType('');
    setCustomPermissions(false);
    setVaultPermission(VAULT_PERMISSIONS.READ_ONLY);
    setPortalPermission(PORTAL_PERMISSIONS.VIEWER);
  }, []);

  // Update permissions based on group type
  const handleGroupTypeChange = useCallback((type) => {
    setGroupType(type);
    if (!customPermissions && type !== 'CUSTOM') {
      // Look up permissions from GROUP_TYPES (uses type keys like 'IMMEDIATE_FAMILY')
      const groupTypeDef = GROUP_TYPES[type];
      if (groupTypeDef?.permissions) {
        setVaultPermission(VAULT_PERMISSIONS.READ_ONLY); // Safe default
        setPortalPermission(PORTAL_PERMISSIONS.VIEWER); // Safe default
      }
    }
  }, [customPermissions]);

  const handleOpenDialog = (type, group) => {
    setDialogType(type);
    setSelectedGroup(group);

    // Populate form fields when editing
    if (type === 'edit' && group) {
      setGroupName(group.name);
      setGroupType(group.type);
      // Check if group has custom permissions
      const hasCustomPerms = group.permissions &&
        (group.permissions.vault || group.permissions.portal);
      setCustomPermissions(hasCustomPerms);
      if (hasCustomPerms) {
        setVaultPermission(group.permissions.vault || VAULT_PERMISSIONS.READ_ONLY);
        setPortalPermission(group.permissions.portal || PORTAL_PERMISSIONS.VIEWER);
      }
    }

    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedGroup(null);
    setSelectedUser(null);
    setDialogType('');
  };

  const handleAddUserToGroup = async () => {
    if (!selectedUser || !selectedGroup) return;

    try {
      setLoading(true);

      // Get family code
      const user = auth.currentUser;
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const familyCode = userDoc.data().familyCode;

      // Use familyGroupService to properly sync both locations
      await familyGroupService.addMemberToGroup(
        familyCode,
        selectedGroup.type,  // Use group type (e.g., 'IMMEDIATE_FAMILY') not ID
        selectedUser.id
      );

      setSuccess(`Added ${selectedUser.email} to ${selectedGroup.name}`);
      loadGroups();
      handleCloseDialog();
    } catch (err) {
      logger.error('Error adding user to group:', err);
      setError('Failed to add user to group');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveUserFromGroup = async (userId) => {
    if (!selectedGroup) return;

    try {
      setLoading(true);

      // Get family code
      const user = auth.currentUser;
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const familyCode = userDoc.data().familyCode;

      // Use familyGroupService to properly sync both locations
      await familyGroupService.removeMemberFromGroup(
        familyCode,
        selectedGroup.type,  // Use group type (e.g., 'IMMEDIATE_FAMILY') not ID
        userId
      );

      setSuccess('User removed from group');
      loadGroups();
      handleCloseDialog();
    } catch (err) {
      logger.error('Error removing user from group:', err);
      setError('Failed to remove user from group');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!selectedGroup) return;

    // Prevent deletion of default groups
    if (selectedGroup.isDefault) {
      setError('Default groups cannot be deleted. They are required for permissions management.');
      return;
    }

    try {
      setLoading(true);
      const user = auth.currentUser;
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const familyCode = userDoc.data().familyCode;

      // Remove group from all members first (nested structure)
      for (const member of (selectedGroup.memberDetails || [])) {
        try {
          await familyGroupService.removeMemberFromGroup(familyCode, selectedGroup.id, member.id);
        } catch (memberErr) {
          logger.error(`Error removing member ${member.id} from group:`, memberErr);
        }
      }

      // Delete from nested structure: families/{familyCode}/groups/{groupId}
      const familyRef = doc(db, 'families', familyCode);
      const groupRef = doc(familyRef, 'groups', selectedGroup.id);
      await deleteDoc(groupRef);

      setSuccess('Group deleted successfully');
      setGroups(groups.filter(g => g.id !== selectedGroup.id));
      handleCloseDialog();
    } catch (err) {
      logger.error('Error deleting group:', err);
      setError('Failed to delete group');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async () => {
    if (!groupName || !groupType) return;

    try {
      setLoading(true);
      const user = auth.currentUser;
      if (!user) throw new Error('No authenticated user');

      // Get familyCode for the current user
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) throw new Error('User not found');
      const familyCode = userDoc.data().familyCode;
      if (!familyCode) throw new Error('No family code found');

      // Normalize the group name to Title Case
      const normalizedName = toTitleCase(groupName);

      // For custom groups, generate a type key from the group name
      // e.g., "Cousins" -> "COUSINS", "Book Club" -> "BOOK_CLUB"
      const isCustomType = groupType === 'CUSTOM';
      const typeKey = isCustomType
        ? normalizedName.toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_|_$/g, '')
        : groupType;

      // Build permissions - use custom if toggled, otherwise use GROUP_TYPES defaults
      const permissions = customPermissions
        ? { vault: vaultPermission, portal: portalPermission }
        : (GROUP_TYPES[groupType]?.permissions || GROUP_TYPES.EXTENDED_FAMILY.permissions);

      const groupData = {
        name: normalizedName,
        description: isCustomType ? `Custom group: ${normalizedName}` : (GROUP_TYPES[groupType]?.description || ''),
        permissions: permissions,
        members: [user.uid],   // Creator is automatically added
        admins: [user.uid],    // Creator is automatically an admin
        createdBy: user.uid,
        isDefault: false,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // Write to nested structure: families/{familyCode}/groups/{typeKey}
      await familyGroupService.createFamilyGroup(familyCode, typeKey, groupData);

      // Also add group to creator's member record
      const familyRef = doc(db, 'families', familyCode);
      const memberRef = doc(familyRef, 'members', user.uid);
      const memberDoc = await getDoc(memberRef);
      if (memberDoc.exists()) {
        await updateDoc(memberRef, {
          groups: arrayUnion(typeKey),
          updatedAt: new Date()
        });
      }

      setSuccess(`Created new group: ${normalizedName}`);
      loadGroups();
      handleCloseDialog();
      resetForm();
    } catch (err) {
      logger.error('Error creating group:', err);
      setError(`Failed to create group: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditGroup = async () => {
    if (!selectedGroup || !groupName) return;

    // Prevent editing default group names and types
    if (selectedGroup.isDefault) {
      setError('Default groups cannot be renamed. You can only edit permissions.');
      return;
    }

    try {
      setLoading(true);
      const user = auth.currentUser;
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const familyCode = userDoc.data().familyCode;

      const normalizedName = toTitleCase(groupName);
      const updateData = {
        name: normalizedName
      };

      // If custom permissions are set, update them
      if (customPermissions) {
        updateData.permissions = {
          vault: vaultPermission,
          portal: portalPermission
        };
      }

      // Use nested structure: families/{familyCode}/groups/{groupId}
      await familyGroupService.updateFamilyGroup(familyCode, selectedGroup.id, updateData);

      setSuccess(`Updated group: ${normalizedName}`);
      loadGroups();
      handleCloseDialog();
      resetForm();
    } catch (err) {
      logger.error('Error updating group:', err);
      setError('Failed to update group');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to format permissions
  const formatPermissions = (permissions) => {
    if (!permissions) return 'None';
    if (typeof permissions === 'object') {
      if (permissions.name) return permissions.name;
      return Object.entries(permissions)
        .filter(([_, value]) => value === true)
        .map(([key]) => key.charAt(0).toUpperCase() + key.slice(1))
        .join(', ') || 'None';
    }
    return permissions.toString();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" component="h2">Group Management</Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('create')}
            >
              Create Group
            </Button>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Group Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Permissions</TableCell>
                    <TableCell>Members</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groups.map((group) => (
                    <TableRow key={group.id}>
                      <TableCell>{group.name}</TableCell>
                      <TableCell>
                        <Chip
                          label={group.type}
                          color={group.type === 'ADMIN' ? 'error' : 'primary'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {(() => {
                            // Fall back to GROUP_TYPES defaults when stored permissions are empty
                            const stored = group.permissions || {};
                            const fallback = GROUP_TYPES[group.type]?.permissions || {};
                            const vault = (stored.vault && Object.keys(stored.vault).length > 0)
                              ? stored.vault : fallback.vault;
                            const portal = (stored.portal && Object.keys(stored.portal).length > 0)
                              ? stored.portal : fallback.portal;
                            return <>Vault: {formatPermissions(vault)}<br />Portal: {formatPermissions(portal)}</>;
                          })()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${group.members?.length || 0} members`}
                          size="small"
                          onClick={() => handleOpenDialog('members', group)}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog('edit', group)}
                          color="primary"
                          title="Edit group"
                          sx={{ mr: 1 }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog('delete', group)}
                          color="error"
                          disabled={group.isDefault}
                          title={group.isDefault ? 'Default groups cannot be deleted' : 'Delete group'}
                          sx={{
                            opacity: group.isDefault ? 0.3 : 1,
                            cursor: group.isDefault ? 'not-allowed' : 'pointer'
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
            <DialogTitle>
              {dialogType === 'create' && 'Create New Group'}
              {dialogType === 'edit' && 'Edit Group'}
              {dialogType === 'addUser' && 'Add User to Group'}
              {dialogType === 'delete' && 'Delete Group'}
              {dialogType === 'members' && `${selectedGroup?.name} Members`}
            </DialogTitle>
            <DialogContent>
              {(dialogType === 'create' || dialogType === 'edit') && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                  {dialogType === 'edit' && selectedGroup?.isDefault && (
                    <Alert severity="info">
                      Default groups cannot be renamed or have their type changed. You can only edit permissions.
                    </Alert>
                  )}
                  <TextField
                    label="Group Name"
                    value={groupName}
                    onChange={(e) => setGroupName(toTitleCase(e.target.value))}
                    fullWidth
                    required
                    disabled={dialogType === 'edit' && selectedGroup?.isDefault}
                    helperText={dialogType === 'edit' && selectedGroup?.isDefault ? 'Default group name cannot be changed' : 'Name will be formatted as Title Case (e.g. "Cousins")'}
                  />
                  <FormControl fullWidth required>
                    <InputLabel>Group Type</InputLabel>
                    <Select
                      value={groupType}
                      onChange={(e) => handleGroupTypeChange(e.target.value)}
                      label="Group Type"
                      disabled={dialogType === 'edit'}
                    >
                      <MenuItem value="CUSTOM"><em>Custom Group</em></MenuItem>
                      {Object.entries(GROUP_TYPES).map(([type, config]) => (
                        <MenuItem key={type} value={type}>{config.name} (template)</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {groupType === 'CUSTOM' && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      Custom group will use Extended Family permissions by default. Toggle "Custom Permissions" to adjust.
                    </Alert>
                  )}
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={customPermissions}
                        onChange={(e) => setCustomPermissions(e.target.checked)}
                      />
                    }
                    label="Custom Permissions"
                  />

                  <Typography variant="subtitle1" sx={{ mt: 2 }}>Vault Permissions</Typography>
                  <FormControl fullWidth>
                    <InputLabel>Vault Access Level</InputLabel>
                    <Select
                      value={vaultPermission}
                      onChange={(e) => setVaultPermission(e.target.value)}
                      label="Vault Access Level"
                      disabled={!customPermissions}
                    >
                      {Object.entries(VAULT_PERMISSIONS).map(([key, value]) => (
                        <MenuItem key={key} value={value}>{key.replace(/_/g, ' ')}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Typography variant="subtitle1" sx={{ mt: 2 }}>Portal Permissions</Typography>
                  <FormControl fullWidth>
                    <InputLabel>Portal Access Level</InputLabel>
                    <Select
                      value={portalPermission}
                      onChange={(e) => setPortalPermission(e.target.value)}
                      label="Portal Access Level"
                      disabled={!customPermissions}
                    >
                      {Object.entries(PORTAL_PERMISSIONS).map(([key, value]) => (
                        <MenuItem key={key} value={value}>{key.replace(/_/g, ' ')}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
              {dialogType === 'addUser' && (
                <Autocomplete
                  options={users}
                  getOptionLabel={(option) => option.email}
                  onChange={(event, newValue) => setSelectedUser(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select User"
                      margin="normal"
                      fullWidth
                    />
                  )}
                />
              )}
              {dialogType === 'delete' && (
                <Typography>
                  Are you sure you want to delete {selectedGroup?.name}? This action cannot be undone.
                </Typography>
              )}
              {dialogType === 'members' && selectedGroup && (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ mb: 2 }}>
                    <Autocomplete
                      options={users.filter(user => 
                        !selectedGroup.members?.includes(user.id)
                      )}
                      getOptionLabel={(option) => option.email}
                      onChange={(event, newValue) => setSelectedUser(newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Add New Member"
                          fullWidth
                        />
                      )}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{ mt: 1 }}
                      disabled={!selectedUser}
                      onClick={handleAddUserToGroup}
                    >
                      Add Member
                    </Button>
                  </Box>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>Current Members:</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedGroup.memberDetails?.map((member) => (
                      <Chip
                        key={member.id}
                        label={member.email}
                        onDelete={() => handleRemoveUserFromGroup(member.id)}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>
                {dialogType === 'members' ? 'Close' : 'Cancel'}
              </Button>
              {dialogType !== 'members' && (
                <Button
                  onClick={() => {
                    if (dialogType === 'create') handleCreateGroup();
                    else if (dialogType === 'edit') handleEditGroup();
                    else if (dialogType === 'addUser') handleAddUserToGroup();
                    else if (dialogType === 'delete') handleDeleteGroup();
                  }}
                  variant="contained"
                  color={dialogType === 'delete' ? 'error' : 'primary'}
                >
                  {dialogType === 'create' ? 'Create' :
                   dialogType === 'edit' ? 'Save' :
                   dialogType === 'addUser' ? 'Add' : 'Delete'}
                </Button>
              )}
            </DialogActions>
          </Dialog>
        </Paper>
      </Box>
    </Container>
  );
};

export default GroupManagement; 